import React from "react";
import { Dialog } from "components/main/utils/Dialog";
import { Button } from "components/common/Button";
import { SegmentedControl } from "components/common/SegmentedControl";
import { SeatStepper } from "components/main/organizations/SeatStepper";
import { plural } from "components/common/ImpactRow";
import type { CurrentPlan, SeatsPreview } from "@/lib/api/organizations";
import { formatMoneyCents } from "@/lib/format";
import {
  formatSeatMoney,
  INCL_TAX,
  PLUS_TAX,
  type SeatRate,
} from "components/main/organizations/seatRate";
import { Quoting } from "components/main/organizations/Quoting";

const LedgerRow: React.FC<{
  label: string;
  value: React.ReactNode;
  strong?: boolean;
}> = ({ label, value, strong = false }) => (
  <span className="flex items-baseline justify-between gap-3">
    <span className="text-fg-muted-regular">{label}</span>
    <span className={strong ? "text-figure" : "text-figure-muted"}>
      {value}
    </span>
  </span>
);

const PRESETS = [1, 2, 5];

/// When the prorated difference is collected: monthly on the next invoice,
/// yearly charged now.
const chargeTiming = (billingPeriod: string, wanted: number) =>
  billingPeriod === "yearly"
    ? `Charged now, prorated to the end of this cycle. All ${wanted.toLocaleString()} seats then renew together.`
    : `Available now. The prorated cost for the rest of this cycle is added to your next invoice, and all ${wanted.toLocaleString()} seats then renew together.`;

/// A reduction waits for the boundary: the seats are paid for until then (D57).
const reduceTiming = (wanted: number, on: string | null) =>
  `The seats you have stay until ${on ?? "the end of this cycle"}, when the subscription renews at ${wanted.toLocaleString()}. Nothing is charged for the change.`;

/// Every figure is Paddle's: the subscription's rate and its preview of the change.
export const SeatsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  /// Seats already bought.
  held: number;
  /// The lowest allowed: the roster when a reduction is offered, otherwise the
  /// higher of `held` (a paid seat runs to cycle end) and the roster.
  floor: number;
  wanted: number;
  onWantedChange: (seats: number) => void;
  plan: CurrentPlan;
  seatRate: SeatRate | null;
  /// Paddle's figures for `wanted`: `"quoting"` until they land, `null` where
  /// nothing is picked or Paddle could not price it.
  preview?: SeatsPreview | "quoting" | null;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  /// The formatted boundary a reduction lands on; `null` offers none.
  reduceOn?: string | null;
}> = ({
  open,
  onClose,
  held,
  floor,
  wanted,
  onWantedChange,
  plan,
  seatRate,
  preview = null,
  busy,
  error,
  onConfirm,
  reduceOn = null,
}) => {
  const added = wanted - held;
  const short = floor > held;
  const mayReduce = floor < held;
  const reducing = added < 0;
  const yearly = plan.billing_period === "yearly";
  const quoting = preview === "quoting";
  const figures = quoting ? null : preview;
  const tax = seatRate?.taxOnTop ? `, ${PLUS_TAX}` : "";
  const inclTax = seatRate?.taxOnTop ? `, ${INCL_TAX}` : "";
  // Paddle's figure for the new count; the subscription's rate × seats only
  // where none was asked or Paddle could not answer.
  const bill = figures
    ? formatMoneyCents(figures.recurring, figures.currency_code)
    : seatRate && !quoting
      ? formatSeatMoney(seatRate, wanted)
      : null;
  const billTax = figures ? inclTax : tax;
  const priceSuffix = bill ? ` · ${bill}/${yearly ? "yr" : "mo"}` : "";
  // Only an increase is charged before renewal: yearly at once, monthly on the
  // next invoice.
  const proratedAmount = yearly
    ? figures?.charged_now
    : figures?.next_invoice_adds;
  const prorated =
    added <= 0 ? null : quoting ? (
      <Quoting width="4rem" />
    ) : figures && proratedAmount != null ? (
      `${formatMoneyCents(proratedAmount, figures.currency_code)}${inclTax}`
    ) : null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={mayReduce ? "Change seats" : "Add seats"}
      showCloseButton={false}
      actions={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={busy}
            disabled={added === 0}
            onClick={onConfirm}
            data-testid="confirm-add-seats"
          >
            {reducing
              ? `Reduce to ${wanted.toLocaleString()} ${plural(wanted, "seat")}`
              : added < 1
                ? "Add seats"
                : `Add ${added} ${plural(added, "seat")}${priceSuffix}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="seat-editor">
        <span className="flex items-center justify-between gap-3">
          <label htmlFor="seats-held" className="text-fg-medium">
            Seats
          </label>
          <SeatStepper
            id="seats-held"
            seats={wanted}
            min={floor}
            onChange={onWantedChange}
          />
        </span>

        {/* Relative to the floor, so pressing a preset twice does not compound. */}
        <span className="flex justify-end" data-testid="seat-presets">
          <SegmentedControl
            options={PRESETS.map((step) => ({
              value: step,
              label: `+${step}`,
            }))}
            value={wanted - held}
            onChange={(step) => onWantedChange(held + step)}
          />
        </span>

        <div className="flex flex-col gap-1 border-t border-border pt-3">
          <LedgerRow
            label="Already bought"
            value={`${held.toLocaleString()} ${plural(held, "seat")}`}
          />
          <LedgerRow
            label={reducing ? "Removing" : "Adding"}
            value={
              added !== 0
                ? `${Math.abs(added).toLocaleString()} ${plural(Math.abs(added), "seat")}`
                : "—"
            }
            strong
          />
          <LedgerRow
            label="New total"
            value={`${wanted.toLocaleString()} ${plural(wanted, "seat")}`}
          />
          {seatRate && (
            <LedgerRow
              label="Rate"
              value={`${formatSeatMoney(seatRate)} /seat · ${plan.billing_period}${tax}`}
            />
          )}
          {(bill || quoting) && (
            <LedgerRow
              label={`New ${plan.billing_period} bill`}
              value={bill ? `${bill}${billTax}` : <Quoting width="5rem" />}
              strong
            />
          )}
          {prorated && (
            <LedgerRow
              label={yearly ? "Charged now" : "Added to next invoice"}
              value={prorated}
            />
          )}
        </div>

        <span className="text-fg-muted-regular-small">
          {added > 0
            ? chargeTiming(plan.billing_period, wanted)
            : reducing
              ? reduceTiming(wanted, reduceOn)
              : mayReduce
                ? "Pick a number above to change seats."
                : "Pick a number above to add seats."}
        </span>
        {wanted <= floor && (
          <span className="text-fg-muted-regular-small">
            {short || mayReduce
              ? `This organization holds ${floor.toLocaleString()} ${plural(floor, "member")} and outstanding invitations, so it can't hold fewer seats than that.`
              : `Seats already bought run to the end of the cycle, so this can't go below ${held.toLocaleString()}. Removing a member frees their seat for the next invitation without changing the bill.`}
          </span>
        )}
        {error && (
          <span className="text-error" data-testid="billing-error">
            {error}
          </span>
        )}
      </div>
    </Dialog>
  );
};
